/* ==========================================================================
   Swaraas — Product page behaviour
   Four small custom elements. No dependencies beyond Dawn's pubsub.js, and
   each one degrades to static server-rendered markup if JS never runs.
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------
     IST clock
     Dispatch cutoffs are a warehouse fact, not a browser fact. A shopper in
     Dubai or New Jersey must see the same cutoff as one in Surat, so every
     date here is computed in Asia/Kolkata regardless of the device clock.
     --------------------------------------------------------------------- */
  var IST_PARTS = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  function istNow() {
    var map = {};
    IST_PARTS.formatToParts(new Date()).forEach(function (p) {
      map[p.type] = p.value;
    });
    // A local-time Date whose wall-clock reads as IST. Only ever used for
    // day-arithmetic and formatting, never for absolute-instant comparison.
    return new Date(
      Number(map.year),
      Number(map.month) - 1,
      Number(map.day),
      Number(map.hour === '24' ? 0 : map.hour),
      Number(map.minute),
      Number(map.second)
    );
  }

  function addDays(date, n) {
    var d = new Date(date.getTime());
    d.setDate(d.getDate() + n);
    return d;
  }

  /** Advance by n working days, optionally treating Sunday as closed. */
  function addWorkingDays(date, n, skipSundays) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < n) {
      d = addDays(d, 1);
      if (skipSundays && d.getDay() === 0) continue;
      added++;
    }
    return d;
  }

  function formatDate(date) {
    return new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }).format(date);
  }

  function formatDateShort(date) {
    return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short' }).format(date);
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  /* =====================================================================
     <swaraas-delivery> — dispatch countdown + dated delivery window
     ===================================================================== */
  class SwaraasDelivery extends HTMLElement {
    connectedCallback() {
      this.cutoffHour = Number(this.dataset.cutoffHour || 14);
      this.minDays = Number(this.dataset.minDays || 4);
      this.maxDays = Number(this.dataset.maxDays || 7);
      this.skipSundays = this.dataset.skipSundays === 'true';

      this.clockEl = this.querySelector('[data-sw-clock]');
      this.dispatchWordEl = this.querySelector('[data-sw-dispatch-word]');
      this.etaEl = this.querySelector('[data-sw-eta]');
      this.orderedEl = this.querySelector('[data-sw-date-ordered]');
      this.shippedEl = this.querySelector('[data-sw-date-shipped]');
      this.deliveredEl = this.querySelector('[data-sw-date-delivered]');

      this.render();
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    /** Dispatch is today if we are before cutoff, else the next open day. */
    dispatchDate(now) {
      var beforeCutoff = now.getHours() < this.cutoffHour;
      var d = beforeCutoff ? new Date(now.getTime()) : addDays(now, 1);
      while (this.skipSundays && d.getDay() === 0) d = addDays(d, 1);
      return { date: d, sameDay: beforeCutoff && d.getDate() === now.getDate() };
    }

    render() {
      var now = istNow();
      var dispatch = this.dispatchDate(now);
      var from = addWorkingDays(dispatch.date, this.minDays, this.skipSundays);
      var to = addWorkingDays(dispatch.date, this.maxDays, this.skipSundays);

      if (this.dispatchWordEl) {
        this.dispatchWordEl.textContent = dispatch.sameDay ? 'today' : formatDate(dispatch.date);
      }
      if (this.etaEl) {
        this.etaEl.textContent = formatDateShort(from) + ' – ' + formatDateShort(to);
      }
      if (this.orderedEl) this.orderedEl.textContent = formatDateShort(now);
      if (this.shippedEl) this.shippedEl.textContent = formatDateShort(dispatch.date);
      if (this.deliveredEl) {
        this.deliveredEl.textContent = formatDateShort(from) + ' – ' + formatDateShort(to);
      }

      this.renderedFor = now.toDateString() + (now.getHours() < this.cutoffHour ? 'a' : 'b');
    }

    tick() {
      var now = istNow();
      var target = new Date(now.getTime());
      target.setHours(this.cutoffHour, 0, 0, 0);
      if (now >= target) target = addDays(target, 1);

      var ms = target.getTime() - now.getTime();
      if (this.clockEl) {
        var h = Math.floor(ms / 3600000);
        var m = Math.floor((ms % 3600000) / 60000);
        var s = Math.floor((ms % 60000) / 1000);
        this.clockEl.textContent = pad(h) + 'h ' + pad(m) + 'm ' + pad(s) + 's';
      }

      // Re-render dates when we roll past the cutoff or past midnight.
      var stamp = now.toDateString() + (now.getHours() < this.cutoffHour ? 'a' : 'b');
      if (stamp !== this.renderedFor) this.render();
    }
  }
  customElements.define('swaraas-delivery', SwaraasDelivery);

  /* =====================================================================
     <swaraas-sticky-atc> — persistent buy bar

     It never owns a form of its own. It submits the real product form, so
     variant selection, quantity, line-item properties, sold-out state and
     Dawn's cart drawer all keep working with zero duplicated logic.
     ===================================================================== */
  class SwaraasStickyAtc extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.button = this.querySelector('[data-sw-sticky-submit]');
      this.buyButton = this.querySelector('[data-sw-sticky-buy]');
      this.priceEl = this.querySelector('[data-sw-sticky-price]');
      this.wasEl = this.querySelector('[data-sw-sticky-was]');
      this.thumbEl = this.querySelector('[data-sw-sticky-thumb]');

      this.button && this.button.addEventListener('click', this.submit.bind(this));
      this.buyButton && this.buyButton.addEventListener('click', this.buyNow.bind(this));

      this.watchAnchor();
      this.watchVariant();
    }

    get form() {
      return document.querySelector('#product-form-' + this.sectionId);
    }

    /* Buy now.

       This button must never implement checkout itself. This store runs a
       third-party checkout (Breeze / sdk.breezesdk.store, loaded at the top of
       layout/theme.liquid), and apps like it work by hooking the REAL buy
       button and the cart. An earlier version of this method posted to
       /cart/add.js and then sent the shopper to /checkout, which silently
       bypassed that app and dropped them into Shopify's default checkout —
       the wrong gateway, and invisible from the theme's point of view.

       So: find the actual payment button inside the product form and click it.
       Whatever any app has bound to that button runs exactly as it does when
       the shopper presses it directly. The sticky bar is a remote control, not
       a second checkout.

       Payment configuration is admin-side and stays that way. Nothing here
       chooses a gateway, a method, or a checkout URL. */
    buyNow() {
      var form = this.form;
      if (!form || this.busy) return;

      // The real dynamic-checkout button, i.e. whatever the store actually
      // uses. Skip disabled/skeleton states — those are not ready to click.
      var payButton = form.querySelector(
        '.shopify-payment-button__button:not([disabled]):not(.shopify-payment-button__button--hidden)'
      );

      if (payButton) {
        payButton.click();
        return;
      }

      /* No payment button on the page — dynamic checkout switched off, or the
         app has not finished mounting. Fall back to the plain add-to-cart path
         rather than inventing a checkout URL: the shopper lands in the cart,
         where the same third-party checkout is still in control. */
      this.submitForm();
    }

    submitForm() {
      var form = this.form;
      if (!form) return;
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        var real = document.querySelector('#ProductSubmitButton-' + this.sectionId);
        real && real.click();
      }
    }

    /* Show the bar only once the real Add to cart button has left the
       viewport — before that it would compete with itself. */
    watchAnchor() {
      var anchor = document.querySelector('#ProductSubmitButton-' + this.sectionId);
      if (!anchor || !('IntersectionObserver' in window)) {
        this.setVisible(true);
        return;
      }

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          self.setVisible(!entries[0].isIntersecting);
        },
        { rootMargin: '0px 0px -40px 0px' }
      );
      this.observer.observe(anchor);
    }

    setVisible(visible) {
      this.dataset.visible = visible ? 'true' : 'false';
      this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    watchVariant() {
      if (typeof subscribe !== 'function') return;
      var self = this;
      this.unsubscribe = subscribe('variant-change', function (event) {
        var data = event && event.data;
        if (!data || data.sectionId !== self.sectionId) return;
        self.update(data.variant, data.html);
      });
    }

    update(variant, html) {
      if (!variant) {
        this.setSoldOut(true);
        return;
      }

      // Reuse the price the section just rendered — same currency format,
      // same badges, no second money-formatting implementation to drift.
      var freshPrice = html && html.querySelector('#price-' + this.sectionId + ' .price-item--regular');
      if (this.priceEl && freshPrice) this.priceEl.textContent = freshPrice.textContent.trim();

      if (this.wasEl) {
        var freshWas = html && html.querySelector('#price-' + this.sectionId + ' .price-item--sale');
        var hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.wasEl.hidden = !hasCompare;
        if (hasCompare && freshWas) this.wasEl.textContent = freshWas.textContent.trim();
      }

      if (this.thumbEl && variant.featured_media && variant.featured_media.preview_image) {
        var src = variant.featured_media.preview_image.src;
        this.thumbEl.src = src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=140';
      }

      this.setSoldOut(!variant.available);
    }

    setSoldOut(soldOut) {
      if (!this.button) return;
      this.button.disabled = soldOut;
      this.button.textContent = soldOut
        ? this.button.dataset.labelSoldOut || 'Sold out'
        : this.button.dataset.labelDefault || 'Add to bag';
    }

    submit(event) {
      event.preventDefault();
      var form = this.form;
      if (!form) return;

      // requestSubmit fires the submit event, which is what Dawn's
      // product-form.js listens for. form.submit() would bypass it.
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        var real = document.querySelector('#ProductSubmitButton-' + this.sectionId);
        real && real.click();
      }
    }
  }
  customElements.define('swaraas-sticky-atc', SwaraasStickyAtc);

  /* =====================================================================
     <swaraas-specs> — reveal the long tail of specifications on mobile
     ===================================================================== */
  class SwaraasSpecs extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-sw-spec-grid]');
      this.toggle = this.querySelector('[data-sw-spec-toggle]');
      if (!this.grid || !this.toggle) return;

      this.toggle.hidden = false;
      this.toggle.addEventListener('click', this.onToggle.bind(this));
    }

    onToggle() {
      var collapsed = this.grid.dataset.collapsed === 'true';
      this.grid.dataset.collapsed = collapsed ? 'false' : 'true';
      this.toggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
      this.toggle.textContent = collapsed
        ? this.toggle.dataset.labelLess || 'Show fewer details'
        : this.toggle.dataset.labelMore || 'Show all details';
    }
  }
  customElements.define('swaraas-specs', SwaraasSpecs);

  /* =====================================================================
     <swaraas-live-badge> — simulated live activity

     The numbers are NOT measured. Shopify gives the storefront no visitor
     count, so everything here is generated. See the snippet header for the
     compliance note; this comment is about making it behave believably.

     Real traffic does not jump by a random amount every four seconds. It
     drifts: mostly small changes, occasionally a bigger one, with a pull back
     toward a baseline so the figure does not wander off. The interval is
     jittered too, because anything on an exact metronome reads as a script.
     A viewer who watches for thirty seconds should see something that looks
     like people arriving and leaving, not a slot machine.
     ===================================================================== */
  class SwaraasLiveBadge extends HTMLElement {
    connectedCallback() {
      this.numberEl = this.querySelector('[data-sw-live-number]');
      this.labelEl = this.querySelector('[data-sw-live-label]');
      this.iconEl = this.querySelector('[data-sw-live-icon]');
      if (!this.numberEl || !this.labelEl) return;

      this.min = Number(this.dataset.viewersMin || 50);
      this.max = Number(this.dataset.viewersMax || 150);
      this.drift = Number(this.dataset.drift || 15);
      this.rotateMs = Number(this.dataset.rotate || 4000);
      this.showSales = this.dataset.showSales !== 'false';
      this.viewersText = this.dataset.viewersText || '';
      this.salesText = this.dataset.salesText || '';

      this.baseline = this.rand(this.min, this.max);
      this.viewers = this.baseline;
      this.sales = this.stableSales();
      this.showingViewers = true;

      this.paint();
      this.schedule();

      // Do not run a timer against a tab nobody is looking at.
      this.onVisibility = function () {
        if (document.hidden) {
          clearTimeout(this.timer);
          this.timer = null;
        } else if (!this.timer) {
          this.schedule();
        }
      }.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    rand(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }

    /* The 24-hour figure is held for an hour per product. A number that
       reshuffles on every page view is the tell that gives these away. */
    stableSales() {
      var id = this.dataset.productId || '0';
      var keyN = 'sw_sales_' + id;
      var keyT = 'sw_sales_at_' + id;
      var hour = 3600000;

      try {
        var saved = localStorage.getItem(keyN);
        var when = Number(localStorage.getItem(keyT) || 0);
        if (saved && Date.now() - when < hour) return Number(saved);

        var n = this.rand(Number(this.dataset.salesMin || 1), Number(this.dataset.salesMax || 49));
        localStorage.setItem(keyN, String(n));
        localStorage.setItem(keyT, String(Date.now()));
        return n;
      } catch (e) {
        // Private mode, storage disabled — fall back to a session-only value.
        return this.rand(Number(this.dataset.salesMin || 1), Number(this.dataset.salesMax || 49));
      }
    }

    /* One step of a bounded random walk with mild mean reversion. */
    nextViewers() {
      // Most steps are small; roughly one in five is larger.
      var big = Math.random() < 0.2;
      var magnitude = big ? this.rand(Math.ceil(this.drift / 2), this.drift) : this.rand(1, Math.max(2, Math.round(this.drift / 3)));

      // Pull gently toward the baseline so the figure stays plausible.
      var pull = (this.baseline - this.viewers) / (this.max - this.min || 1);
      var upward = Math.random() < 0.5 + pull;
      var next = this.viewers + (upward ? magnitude : -magnitude);

      if (next < this.min) next = this.min + this.rand(0, 3);
      if (next > this.max) next = this.max - this.rand(0, 3);

      this.viewers = next;
      return next;
    }

    paint() {
      if (this.showingViewers) {
        this.numberEl.textContent = this.viewers;
        this.labelEl.textContent = this.viewersText;
      } else {
        this.numberEl.textContent = this.sales;
        this.labelEl.textContent = this.salesText;
      }
      this.dataset.mode = this.showingViewers ? 'viewers' : 'sales';
    }

    schedule() {
      // Jitter the interval by up to +/-25% so it never ticks like a clock.
      var jitter = this.rotateMs * (0.75 + Math.random() * 0.5);
      this.timer = setTimeout(this.step.bind(this), jitter);
    }

    step() {
      if (this.showSales) {
        this.showingViewers = !this.showingViewers;
      }
      if (this.showingViewers) this.nextViewers();
      this.paint();
      this.schedule();
    }
  }
  customElements.define('swaraas-live-badge', SwaraasLiveBadge);

  /* =====================================================================
     Gallery auto-rotate

     Drives Dawn's own thumbnail buttons rather than scrolling the slider.
     Those buttons call MediaGallery.setActiveMedia(), which is the one path
     that works at every breakpoint — on desktop the viewer is not a slider
     at all, so scrolling it would do nothing there.

     Rotation is a loan of the shopper's attention, not a claim on it:
       - it stops permanently the first time they touch the gallery
       - it pauses on hover, on keyboard focus, and off-screen
       - it never runs under prefers-reduced-motion
       - it pauses while the tab is hidden, so returning to the tab does not
         land on a random image
     ===================================================================== */
  class SwaraasGalleryRotate extends HTMLElement {
    connectedCallback() {
      this.gallery = document.getElementById(this.dataset.gallery);
      if (!this.gallery) return;

      this.thumbs = Array.prototype.slice.call(
        this.gallery.querySelectorAll('[id^="GalleryThumbnails"] [data-target] button')
      );
      // One image is a photo, not a gallery.
      if (this.thumbs.length < 2) return;

      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 5000);
      this.index = 0;
      this.paused = false;

      this.onEnter = this.pause.bind(this);
      this.onLeave = this.resume.bind(this);

      /* Only a REAL interaction stops rotation. advance() clicks a thumbnail
         itself, and that synthetic click bubbles to this same listener —
         without the isTrusted guard the carousel would stop itself on its
         very first tick. Synthetic events always report isTrusted === false. */
      this.onStop = function (event) {
        if (event && event.isTrusted === false) return;
        this.stop();
      }.bind(this);

      this.gallery.addEventListener('mouseenter', this.onEnter);
      this.gallery.addEventListener('mouseleave', this.onLeave);
      this.gallery.addEventListener('focusin', this.onEnter);
      this.gallery.addEventListener('focusout', this.onLeave);

      // Any deliberate interaction ends it for good.
      ['click', 'touchstart', 'keydown'].forEach(function (evt) {
        this.gallery.addEventListener(evt, this.onStop, { passive: true });
      }, this);

      this.onVisibility = function () {
        if (document.hidden) this.pause();
        else this.resume();
      }.bind(this);
      document.addEventListener('visibilitychange', this.onVisibility);

      // Only spend cycles while the gallery is actually on screen.
      if ('IntersectionObserver' in window) {
        var self = this;
        this.observer = new IntersectionObserver(
          function (entries) {
            if (entries[0].isIntersecting) self.resume();
            else self.pause();
          },
          { threshold: 0.35 }
        );
        this.observer.observe(this.gallery);
      } else {
        this.start();
      }
    }

    start() {
      if (this.timer || this.stopped) return;
      this.timer = setInterval(this.advance.bind(this), this.interval);
    }

    advance() {
      if (this.paused || this.stopped) return;
      this.index = (this.index + 1) % this.thumbs.length;
      this.thumbs[this.index].click();
    }

    pause() {
      this.paused = true;
    }

    resume() {
      if (this.stopped) return;
      this.paused = false;
      this.start();
    }

    stop() {
      this.stopped = true;
      clearInterval(this.timer);
      this.timer = null;
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.observer && this.observer.disconnect();
    }
  }
  customElements.define('swaraas-gallery-rotate', SwaraasGalleryRotate);

  /* =====================================================================
     Copy-to-clipboard for discount codes
     A code the shopper has to retype is a code they abandon.
     ===================================================================== */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-sw-copy]');
    if (!btn) return;

    var code = btn.dataset.swCopy;
    var label = btn.querySelector('[data-sw-copy-label]') || btn;
    var original = btn.dataset.original || label.textContent.trim();
    btn.dataset.original = original;

    var done = function () {
      btn.dataset.copied = 'true';
      label.textContent = 'Copied';
      clearTimeout(btn._swTimer);
      btn._swTimer = setTimeout(function () {
        btn.dataset.copied = 'false';
        label.textContent = original;
      }, 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done, function () {});
      return;
    }

    // Fallback for non-secure contexts and older Android browsers
    var scratch = document.createElement('textarea');
    scratch.value = code;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'absolute';
    scratch.style.left = '-9999px';
    document.body.appendChild(scratch);
    scratch.select();
    try {
      document.execCommand('copy');
      done();
    } catch (e) {
      /* leave the code visible — it is printed on the button */
    }
    document.body.removeChild(scratch);
  });
})();
